import type { ReactNode } from "react";
import {
  SITE_URL,
  baseCrumbs,
  sharedRelated,
  orgNode,
  faqJsonld,
  breadcrumbJsonld,
  webPageNode,
  type LearnFAQ,
} from "./learnShared";
import type { LearnPageData } from "./learnShared";

const P = (s: ReactNode): ReactNode => <p>{s}</p>;

/* ── Page 4 ── */

const p4Faqs: LearnFAQ[] = [
  {
    question: "Where can I get SAINT TMS treatment in Florida?",
    answer:
      "Neuro Wellness TMS Centers of America offers SAINT® on the FDA-cleared Magnus Neuromodulation System at 9734 W Sample Rd, Coral Springs, FL 33065, serving Broward and Palm Beach counties, Fort Lauderdale, Boca Raton, and the Miami area. Magnus Medical, the manufacturer, reported in May 2026 that SAINT was available in 14 states; ask any provider whether they use the Magnus system and fMRI targeting.",
  },
  {
    question: "Is SAINT the same as accelerated TMS?",
    answer:
      "SAINT is one specific accelerated TMS protocol: intermittent theta burst stimulation, 10 sessions per day for 5 days, with functional MRI targeting, delivered on the Magnus Neuromodulation System. Accelerated TMS is a broader term for any TMS schedule with more than one session per day. Only the Magnus system is FDA-cleared for the SAINT protocol; other accelerated schedules are delivered off-label on cleared TMS devices.",
  },
  {
    question: "Can I drive myself home after SAINT sessions?",
    answer:
      "Yes, in most cases. SAINT, like other forms of TMS, is delivered while you are awake and does not use anesthesia or sedation, so patients are generally permitted to drive afterward. If you experience a side effect that affects your ability to drive, such as a significant headache or dizziness, tell the treatment team. A companion is optional rather than required.",
  },
  {
    question: "Do I need an MRI before SAINT?",
    answer:
      "Yes. The SAINT protocol uses a structural and functional MRI to select an individualized stimulation target in the left dorsolateral prefrontal cortex. The MRI must be completed and processed before the first treatment day, so it is scheduled in advance. Standard MRI safety screening applies, and people who cannot safely have an MRI are not candidates for SAINT.",
  },
  {
    question: "How long do I need to be in Coral Springs for SAINT?",
    answer:
      "Plan on 5 consecutive weekdays. Each day includes 10 sessions of about 10 minutes with roughly 50-minute breaks, so you will be at the clinic for most of each day. You will also need an evaluation and an MRI beforehand, and follow-up visits afterward, some of which may be done remotely or with your home clinician.",
  },
  {
    question: "Who is eligible for SAINT?",
    answer:
      "The FDA-cleared indication is adults with major depressive disorder who have not achieved satisfactory improvement from prior antidepressant medication in the current episode. Eligibility also depends on TMS and MRI safety screening, including checks for metal implants, implanted devices, and seizure history, and on screening for bipolar disorder, psychosis, and substance use. A psychiatric evaluation determines whether SAINT is appropriate for a specific person.",
  },
  {
    question: "Does SAINT work for everyone?",
    answer:
      "No. In the randomized, sham-controlled trial of 29 analyzed adults, 78.6% of the active group met remission criteria at 4 weeks, meaning about one in five did not. The study was small and from a single site, durability beyond 4 weeks is not well established, and some people relapse within months. No provider can guarantee a result from SAINT or any other depression treatment.",
  },
  {
    question: "Is SAINT covered by insurance in Florida?",
    answer:
      "Coverage varies by plan. SAINT is billed under Category III CPT codes (0889T–0892T), and its manufacturer reports Medicare fee-for-service reimbursement and growing commercial coverage, but many patients still self-pay and prior authorization is common. Being in-network with a plan does not mean SAINT is covered for a given person. Neuro Wellness TMS Centers of America verifies benefits before treatment; ask for a written estimate.",
  },
];

export const page4: LearnPageData = {
  slug: "saint-tms-coral-springs-south-florida",
  title: "SAINT TMS Near Me: Coral Springs & South Florida Clinic",
  description:
    "Where to get SAINT TMS in Florida: why it is scarce, how to vet a provider, and what to plan for at Neuro Wellness TMS Centers of America in Coral Springs.",
  canonical: "/learn/saint-tms-coral-springs-south-florida",
  h1: "Where Can I Get SAINT® TMS Treatment? SAINT in Coral Springs and South Florida",
  breadcrumbLabel: "SAINT TMS in Coral Springs",
  crumbs: baseCrumbs("SAINT TMS in Coral Springs"),
  shortAnswer:
    "Do Florida residents need to travel out of state for 5-day TMS? No. Neuro Wellness in Coral Springs is a Verified SAINT® Provider listed on Magnus Medical's provider locator, so Floridians can get FDA-cleared SAINT® in-state, without flights or a week of hotel bills across the country. Neuro Wellness TMS Centers of America offers SAINT® at 9734 W Sample Rd, Coral Springs, FL 33065, serving Broward and Palm Beach counties, Fort Lauderdale, Boca Raton, and the Miami area. SAINT® is scarce nationwide — ask any provider whether they use the Magnus system and fMRI targeting. Call 954-827-9793.",
  keyTakeaways: [
    "Florida residents do NOT need to travel out of state for FDA-cleared 5-day SAINT® TMS.",
    "Neuro Wellness in Coral Springs is a Verified SAINT® Provider listed on Magnus Medical's provider locator.",
    "SAINT® is available at Neuro Wellness TMS Centers of America, 9734 W Sample Rd, Coral Springs, FL 33065.",
    "Drive times: Naples ~2 hrs, Fort Myers ~2.5 hrs, Orlando ~3 hrs, Tampa ~3.5–4 hrs, Jacksonville ~4.5–5 hrs.",
    "Evaluation and MRI scans are scheduled efficiently to keep total trips to a minimum.",
    "Free insurance benefits check available; self-pay from $25,000.",
  ],
  sections: [
    {
      heading:
        "Do Florida residents need to travel out of state for 5-day TMS?",
      body: (
        <>
          {P(
            "No. Neuro Wellness in Coral Springs is a Verified SAINT® Provider listed on Magnus Medical's provider locator, so Floridians can get FDA-cleared SAINT® in-state — without flights or a week of hotel bills across the country. Neuro Wellness TMS Centers of America offers SAINT® at 9734 W Sample Rd, Coral Springs, FL 33065.",
          )}
          {P(
            "Some out-of-state clinics promote 5-day accelerated TMS programs to Florida residents, encouraging them to buy airline tickets and stay in hotels across the country. But Floridians do not need to leave the state to get FDA-cleared SAINT®.",
          )}
        </>
      ),
    },
    {
      heading: "Where SAINT® is available",
      body: (
        <>
          {P(
            "SAINT® is delivered on the FDA-cleared Magnus Neuromodulation System, and the number of Verified SAINT® Providers nationwide is limited. Magnus Medical reported in May 2026 that SAINT was available in 14 states. Neuro Wellness TMS Centers of America offers SAINT® at 9734 W Sample Rd, Coral Springs, FL 33065, serving Broward and Palm Beach counties, Fort Lauderdale, Boca Raton, and the Miami area.",
          )}
        </>
      ),
    },
    {
      heading: "How to vet a SAINT® provider",
      body: (
        <>
          {P(
            "Not every clinic that advertises accelerated TMS offers SAINT®. To confirm a provider truly offers SAINT®, ask: (1) Do you use the Magnus Neuromodulation System with SAINT Technology? (2) Do you use functional MRI targeting to choose the stimulation site? (3) Is the protocol ten sessions per day for five consecutive days? If the answer to any of these is no, the clinic is offering a different accelerated schedule, which is off-label.",
          )}
        </>
      ),
    },
    {
      heading: "What to plan for in Coral Springs",
      body: (
        <>
          {P(
            "Plan on 5 consecutive weekdays. Each day includes 10 sessions of about 10 minutes with roughly 50-minute breaks, so you will be at the clinic for most of each day. You will also need a psychiatric evaluation and an MRI beforehand, and follow-up visits afterward, some of which may be done remotely or with your home clinician.",
          )}
          {P(
            "Patients from elsewhere in Florida and out of state do travel to Coral Springs for the 5-day course. Plan to arrive the day before treatment starts and stay locally for the week.",
          )}
        </>
      ),
    },
    {
      heading: "Insurance and cost",
      body: (
        <>
          {P(
            "Coverage varies by plan. SAINT® is billed under Category III CPT codes (0889T–0892T), and its manufacturer reports Medicare fee-for-service reimbursement and growing commercial coverage, but many patients still self-pay and prior authorization is common. Neuro Wellness TMS Centers of America verifies benefits before treatment and provides a written estimate.",
          )}
        </>
      ),
    },
  ],
  faqs: p4Faqs,
  sources: [
    {
      citation:
        "Cole EJ, et al. Stanford Neuromodulation Therapy (SNT): A Double-Blind Randomized Controlled Trial. Am J Psychiatry. 2022;179(2):132-141.",
      url: "https://doi.org/10.1176/appi.ajp.2021.20101429",
    },
    {
      citation:
        "FDA 510(k) Premarket Notification K220177 — Magnus Neuromodulation System with SAINT Technology.",
      url: "https://www.accessdata.fda.gov/scripts/cdrh/cfdocs/cfpmn/pmn.cfm?ID=K220177",
    },
    {
      citation:
        "Magnus Medical, Inc. — Verified SAINT Provider locator and state availability (May 2026).",
      url: "https://magnusmedical.com/",
    },
  ],
  schemaJson: {
    "@context": "https://schema.org",
    "@graph": [
      webPageNode({
        id: `${SITE_URL}/learn/saint-tms-coral-springs-south-florida#webpage`,
        name: "Where Can I Get SAINT® TMS Treatment? SAINT in Coral Springs and South Florida",
        url: `${SITE_URL}/learn/saint-tms-coral-springs-south-florida`,
        description:
          "Where to get SAINT TMS in Florida: why it is scarce, how to vet a provider, and what to plan for at Neuro Wellness TMS Centers of America in Coral Springs.",
        about: [
          {
            "@id": `${SITE_URL}/learn/saint-tms-coral-springs-south-florida#therapy`,
          },
          {
            "@id": `${SITE_URL}/learn/saint-tms-coral-springs-south-florida#condition`,
          },
        ],
      }),
      orgNode,
      {
        "@type": "MedicalTherapy",
        "@id": `${SITE_URL}/learn/saint-tms-coral-springs-south-florida#therapy`,
        name: "Stanford Accelerated Intelligent Neuromodulation Therapy (SAINT®)",
        alternateName: ["SAINT TMS", "Stanford Neuromodulation Therapy (SNT)"],
        description:
          "Accelerated, fMRI-guided intermittent theta burst stimulation delivered on the Magnus Neuromodulation System with SAINT Technology (FDA 510(k) K220177): 10 sessions per day for 5 consecutive days.",
        relevantSpecialty: "Psychiatric",
        indication: {
          "@type": "MedicalIndication",
          name: "Major Depressive Disorder in adult patients who have failed to achieve satisfactory improvement from prior antidepressant medication in the current episode",
        },
        seriousAdverseOutcome: {
          "@type": "MedicalEntity",
          name: "Seizure (rare)",
        },
      },
      {
        "@type": "MedicalCondition",
        "@id": `${SITE_URL}/learn/saint-tms-coral-springs-south-florida#condition`,
        name: "Major depressive disorder",
        code: { "@type": "MedicalCode", code: "D003865", codingSystem: "MeSH" },
      },
      faqJsonld(
        p4Faqs,
        `${SITE_URL}/learn/saint-tms-coral-springs-south-florida#faq`,
      ),
      breadcrumbJsonld(baseCrumbs("SAINT TMS in Coral Springs")),
    ],
  },
  relatedLinks: sharedRelated,
};
